import { getDropboxVideoMetadata } from './dropbox/metadata';
import { isDropboxHost } from './dropbox/links';
import { parseFrameioUrl } from './frameio/links';
import { getFrameioShareVideos } from './frameio/shareVideos';
import { parseMediasiloUrl } from './mediasilo/links';
import { getMediasiloReviewVideos } from './mediasilo/reviewVideos';
import { authAxios } from './axios';
import { parseMedia } from '@remotion/media-parser';
import type { FrameioShareVideos } from '@/queries/frameio';

const CORS_PROXY_URL = 'https://cors.autoclipper.live';

// Video platform detection types
export type VideoPlatform = 'youtube' | 'instagram' | 'twitch' | 'google-drive' | 'dropbox' | 'frameio' | 'mediasilo' | 'http' | 'unknown';

export interface VideoFormat {
  format_id: string;
  ext: string;
  width?: number;
  height?: number;
  fps?: number;
  vcodec?: string;
  acodec?: string;
  format?: string;
  format_note?: string;
  filesize?: number;
  url: string;
  has_audio?: boolean;
  has_video?: boolean;
}

export interface VideoChannelInfo {
  channel_id: string;
  name: string;
  url?: string;
  thumbnail?: string;
  handle?: string;
  follower_count?: number;
}

export interface VideoMetadata {
  source_size_bytes?: number;
  duration: number; // in seconds
  title?: string;
  description?: string;
  thumbnail?: string;
  platform: VideoPlatform;
  url: string;
  formats?: VideoFormat[];
  best_video_format?: VideoFormat;
  best_audio_format?: VideoFormat;
  live_status?: 'is_live' | 'is_upcoming' | 'not_live' | 'was_live' | 'post_live';
  width?: number;
  height?: number;
  tags?: string[];
  channel?: VideoChannelInfo;
  channels?: VideoChannelInfo[];
  /** Folder listing for share imports (Frame.io, MediaSilo). */
  shareVideos?: FrameioShareVideos;
}

/**
 * Detects the platform of a video URL
 */
export function detectVideoPlatform(url: string): VideoPlatform {
  if (!url) return 'unknown';

  // Classify the actual host before legacy platform strings and generic HTTP.
  try {
    let parsed: URL;
    try { parsed = new URL(url); } catch (error) {
      if (/^[a-z][a-z0-9+.-]*:/i.test(url)) throw error;
      parsed = new URL(`https://${url}`);
    }
    if (!parsed.hostname) parsed = new URL(`https://${url}`);
    if (isDropboxHost(parsed)) return parsed.protocol === 'https:' ? 'dropbox' : 'unknown';

    const frameio = parseFrameioUrl(parsed.href);
    if (frameio.kind === 'share') return 'frameio';
    if (frameio.kind === 'unsupported') return 'unknown';

    const mediasilo = parseMediasiloUrl(parsed.href);
    if (mediasilo.kind === 'review') return 'mediasilo';
    if (mediasilo.kind === 'unsupported') return 'unknown';
  } catch {
    // A malformed destination must never reach a browser parser or legacy API.
    return 'unknown';
  }

  const lowerUrl = url.toLowerCase();

  // YouTube patterns
  if (lowerUrl.includes('youtube.com') || lowerUrl.includes('youtu.be')) {
    return 'youtube';
  }

  // Instagram patterns
  if (lowerUrl.includes('instagram.com')) {
    return 'instagram';
  }

  // Twitch patterns
  if (lowerUrl.includes('twitch.tv')) {
    return 'twitch';
  }

  // Google Drive patterns
  if (lowerUrl.includes('drive.google.com')) {
    return 'google-drive';
  }

  // Check if it's a direct video URL
  if (lowerUrl.match(/\.(mp4|avi|mov|wmv|flv|webm|mkv)(\?.*)?$/)) {
    return 'http';
  }

  // Check if it's an HTTP/HTTPS URL (could be a video)
  if (lowerUrl.startsWith('http://') || lowerUrl.startsWith('https://')) {
    return 'http';
  }

  return 'unknown';
}

/**
 * Gets video metadata using HTML5 video element for HTTP videos
 */
async function parseMediaFromUrl(src: string) {
  return parseMedia({
    src,
    fields: {
      durationInSeconds: true,
      dimensions: true,
    },
  });
}

async function getHttpVideoMetadata(url: string): Promise<VideoMetadata> {
  let media;
  try {
    media = await parseMediaFromUrl(url);
  } catch {
    // Retry through CORS proxy if direct fetch fails (e.g. no CORS headers).
    // Use host:port format instead of https://host to avoid double-slash
    // collapsing by reverse proxies (Traefik/Nginx). cors-anywhere defaults
    // to https when port 443 is specified.
    let parsed: URL;
    try { parsed = new URL(url); } catch (error) {
      if (/^[a-z][a-z0-9+.-]*:/i.test(url)) throw error;
      parsed = new URL(`https://${url}`);
    }
    if (!parsed.hostname) parsed = new URL(`https://${url}`);
    const hostWithPort = parsed.port
      ? parsed.host
      : `${parsed.hostname}:${parsed.protocol === 'https:' ? '443' : '80'}`;
    const proxiedUrl = `${CORS_PROXY_URL}/${hostWithPort}${parsed.pathname}${parsed.search}`;
    media = await parseMediaFromUrl(proxiedUrl);
  }
  return {
    duration: media.durationInSeconds,
    platform: 'http',
    url,
    title: `Video (${Math.round(media.durationInSeconds)}s)`,
    live_status: 'not_live',
    width: media.dimensions.width,
    height: media.dimensions.height,
  };
}

/**
 * Selects the best video format (preferably mp4/webm with audio)
 */
function selectBestVideoFormat(formats: VideoFormat[]): VideoFormat | undefined {
  if (!formats || formats.length === 0) return undefined;

  // Filter for video formats with audio
  const videoFormatsWithAudio = formats.filter(f =>
    f.has_video && f.has_audio &&
    (f.ext === 'mp4' || f.ext === 'webm')
    && (f.format?.match(/original/i) || f.format_note?.match(/default/i))
  );

  if (videoFormatsWithAudio.length > 0) {
    // Sort by quality (higher resolution and better codec preference)
    return videoFormatsWithAudio.sort((a, b) => {
      const resolutionA = (a.width || 0) * (a.height || 0);
      const resolutionB = (b.width || 0) * (b.height || 0);

      // Prefer mp4 over webm if resolution is similar
      if (Math.abs(resolutionA - resolutionB) < 100000) {
        if (a.ext === 'mp4' && b.ext !== 'mp4') return -1;
        if (b.ext === 'mp4' && a.ext !== 'mp4') return 1;
      }

      return resolutionB - resolutionA;
    })[0];
  }

  // Fallback to any video format with good quality
  const videoFormats = formats.filter(f =>
    f.has_video && (f.ext === 'mp4' || f.ext === 'webm')
  );

  if (videoFormats.length > 0) {
    return videoFormats.sort((a, b) => {
      const resolutionA = (a.width || 0) * (a.height || 0);
      const resolutionB = (b.width || 0) * (b.height || 0);
      return resolutionB - resolutionA;
    })[0];
  }

  return undefined;
}

/**
 * Selects the best audio format
 */
function selectBestAudioFormat(formats: VideoFormat[]): VideoFormat | undefined {
  if (!formats || formats.length === 0) return undefined;

  const audioFormats = formats.filter(f =>
    f.has_audio && !f.has_video &&
    (f.ext === 'm4a' || f.ext === 'webm' || f.ext === 'mp3')
    && (f.format?.match(/original/i) || f.format_note?.match(/default/i))
  );

  if (audioFormats.length > 0) {
    // Prefer m4a, then webm, then mp3
    return audioFormats.sort((a, b) => {
      const extPriority = { 'm4a': 3, 'webm': 2, 'mp3': 1 };
      return (extPriority[b.ext as keyof typeof extPriority] || 0) -
        (extPriority[a.ext as keyof typeof extPriority] || 0);
    })[0];
  }

  return undefined;
}

/**
 * Gets video metadata from the backend API for platform videos
 */
async function getPlatformVideoMetadata(url: string): Promise<VideoMetadata> {
  try {
    const response = await authAxios.get('/api/youtube/metadata', {
      params: { videoURL: url }
    });

    const data = response.data;
    const platform = detectVideoPlatform(url);

    // Process formats if available
    let formats: VideoFormat[] = [];
    let bestVideoFormat: VideoFormat | undefined;
    let bestAudioFormat: VideoFormat | undefined;

    if (data.formats && Array.isArray(data.formats)) {
      formats = data.formats.map((format: any) => ({
        format_id: format.format_id || '',
        ext: format.ext || '',
        width: format.width,
        height: format.height,
        fps: format.fps,
        vcodec: format.vcodec,
        acodec: format.acodec,
        filesize: format.filesize,
        format: format.format,
        format_note: format.format_note,
        url: format.url || '',
        has_audio: format.acodec && format.acodec !== 'none',
        has_video: format.vcodec && format.vcodec !== 'none',
      }));

      bestVideoFormat = selectBestVideoFormat(formats);
      bestAudioFormat = selectBestAudioFormat(formats);
    }

    return {
      duration: data.duration || 0,
      title: data.title || data.fulltitle,
      description: data.description,
      thumbnail: data.thumbnail,
      platform,
      url,
      formats,
      best_video_format: bestVideoFormat,
      best_audio_format: bestAudioFormat,
      live_status: data.live_status,
      tags: Array.isArray(data.tags) ? data.tags : [],
      channel: data.channel_id ? {
        channel_id: data.channel_id,
        name: (typeof data.channel === 'object' && data.channel?.name) || (typeof data.channel === 'string' && data.channel) || data.uploader || '',
        url: data.channel_url || data.uploader_url || (typeof data.channel === 'object' && data.channel?.url) || undefined,
        thumbnail: data.channel_thumbnail || undefined,
        handle: data.uploader_id || undefined,
        follower_count: data.channel_follower_count || (typeof data.channel === 'object' && data.channel?.follower_count) || undefined,
      } : undefined,
      channels: Array.isArray(data.channels) ? data.channels.map((ch: any) => ({
        channel_id: ch.id || ch.channel_id || '',
        name: ch.name || '',
        url: ch.url || undefined,
        thumbnail: ch.thumbnail || undefined,
        handle: ch.handle || undefined,
        follower_count: ch.follower_count || ch.subscriber_count || undefined,
      })) : [],
    };
  } catch (error) {
    console.error('Failed to fetch video metadata from API:', error);
    throw new Error('Failed to fetch video metadata');
  }
}

/**
 * Main function to get video metadata regardless of platform
 */
export async function getVideoMetadata(url: string): Promise<VideoMetadata> {
  const platform = detectVideoPlatform(url);

  if (platform === 'unknown') {
    throw new Error('Unsupported video URL format');
  }

  if (platform === 'dropbox') return getDropboxVideoMetadata(url);

  if (platform === 'frameio') return getFrameioShareVideos(url);

  if (platform === 'mediasilo') return getMediasiloReviewVideos(url);

  if (platform === 'http') {
    return getHttpVideoMetadata(url);
  }

  // For platform videos (YouTube, Instagram, Twitch, Google Drive)
  return getPlatformVideoMetadata(url);
}

/**
 * Formats duration from seconds to readable string
 */
export function formatVideoDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }

  return `${minutes}:${secs.toString().padStart(2, '0')}`;
}

/**
 * Formats duration from seconds to minutes
 */
export function formatVideoDurationInMinutes(seconds: number): string {
  const minutes = Math.ceil(seconds / 60);
  return `${minutes} minuto${minutes !== 1 ? 's' : ''}`;
}

/**
 * Formats seconds as "Xh Ym", omitting minutes when 0 (e.g. "12h")
 */
export function formatHoursMinutes(seconds: number): string {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

/**
 * Calculates estimated processing time based on video duration
 */
export function calculateProcessingTime(duration: number): string {
  // Rough estimate: processing takes about 1/10 of video duration
  const estimatedMinutes = Math.ceil(duration / 600); // 10 minutes of video = 1 minute processing
  const hours = Math.floor(estimatedMinutes / 60);
  const minutes = estimatedMinutes % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  return `${minutes}m`;
}
